import { useState } from 'react';
import { Link, useNavigate } from "react-router-dom";
import { CheckCircleIcon, ArrowLeftIcon } from "@heroicons/react/24/solid";
import { postRequest, putRequest } from "../../Requests/postRequests";
import { useDispatch } from "react-redux";
import { show } from '../../slices/toastSlice';
import Logo from "../../assets/images/logo.png";

function ForgotPassword() {

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [email, setEmail] = useState("");
    const [code, setCode] = useState("");
    const [password, setPassword] = useState("");
    const [confirm_pass, setConfirm_pass] = useState("");
    const [stepOne, setStepOne] = useState(true);
    const [stepTwo, setStepTwo] = useState(false);
    const [success, setSuccess] = useState(false);
    const [process, setProcess] = useState(false);

    const handleRequest = async (e) => {
        e.preventDefault();
        setProcess(true);

        const res = await postRequest(`/auth/staff/forgot_password`, { email });

        if (res?.err) {
            setProcess(false);
            dispatch(show({ type: "error", state: true, message: res.err }));
            return;
        }

        if (res?.message) {
            setStepOne(false);
            setStepTwo(true);
        }

        setProcess(false);
    };

    const handleNewPassRequest = async (e) => {
        e.preventDefault();
        setProcess(true);

        if (confirm_pass !== password || !confirm_pass || !password) {
            setProcess(false);
            dispatch(show({
                type: "error",
                state: true,
                message: confirm_pass !== password ? "Passwords do not match" : "Password fields cannot be empty",
            }));
            return;
        }

        const res = await putRequest(`/auth/staff/reset_password`, { email, code, password });

        if (res?.err) {
            setProcess(false);
            dispatch(show({ type: "error", state: true, message: res.err }));
            return;
        }

        setSuccess(true);
        dispatch(show({ type: "success", state: true, message: res?.message }));
        navigate("/login");
        setProcess(false);
    };

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-gray-950 flex items-center justify-center p-4">
            <div className="w-full max-w-sm">

                {/* Logo */}
                <div className="flex justify-center mb-8">
                    <img src={Logo} alt="CedisPay" className="h-10 w-auto object-contain" />
                </div>

                {/* Card */}
                <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-8">

                    {/* Step 1 — Email */}
                    {stepOne && (
                        <>
                            <div className="mb-7">
                                <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
                                    Reset password
                                </h1>
                                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                    Enter your email and we'll send a verification code
                                </p>
                            </div>

                            <form onSubmit={handleRequest} className="space-y-5">
                                <div>
                                    <label htmlFor="email" className="form-label">Email address</label>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        autoComplete="email"
                                        required
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="form-input"
                                        placeholder="you@cedispay.com"
                                    />
                                </div>

                                <button
                                    type="submit"
                                    disabled={process}
                                    className="w-full h-11 flex items-center justify-center rounded-xl bg-light-blue hover:bg-dark-blue text-white text-sm font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed mt-2"
                                >
                                    {process ? (
                                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                        </svg>
                                    ) : (
                                        "Send code"
                                    )}
                                </button>
                            </form>
                        </>
                    )}

                    {/* Step 2 — Code + New Password */}
                    {stepTwo && (
                        <>
                            <div className="mb-7">
                                <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
                                    Set new password
                                </h1>
                                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                    Enter the code sent to <span className="font-medium text-gray-700 dark:text-gray-300">{email}</span>
                                </p>
                            </div>

                            <form onSubmit={handleNewPassRequest} className="space-y-5">
                                <div>
                                    <label htmlFor="code" className="form-label">Verification code</label>
                                    <input
                                        type="number"
                                        id="code"
                                        name="code"
                                        required
                                        value={code}
                                        onChange={(e) => setCode(e.target.value)}
                                        className="form-input"
                                        placeholder="••••"
                                        maxLength={4}
                                    />
                                </div>

                                <div>
                                    <label htmlFor="password" className="form-label">New password</label>
                                    <input
                                        type="password"
                                        id="password"
                                        name="password"
                                        required
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="form-input"
                                        placeholder="••••••••"
                                    />
                                </div>

                                <div>
                                    <label htmlFor="confirm_pass" className="form-label">Confirm password</label>
                                    <input
                                        type="password"
                                        id="confirm_pass"
                                        name="confirm_pass"
                                        required
                                        value={confirm_pass}
                                        onChange={(e) => setConfirm_pass(e.target.value)}
                                        className="form-input"
                                        placeholder="••••••••"
                                    />
                                </div>

                                <button
                                    type="submit"
                                    disabled={process}
                                    className="w-full h-11 flex items-center justify-center rounded-xl bg-light-blue hover:bg-dark-blue text-white text-sm font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed mt-2"
                                >
                                    {process ? (
                                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                        </svg>
                                    ) : success ? (
                                        <CheckCircleIcon className="w-5 h-5" />
                                    ) : (
                                        "Update password"
                                    )}
                                </button>
                            </form>
                        </>
                    )}
                </div>

                {/* Footer */}
                <p className="mt-5 text-center">
                    <Link
                        to="/login"
                        className="inline-flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                    >
                        <ArrowLeftIcon className="w-3.5 h-3.5" />
                        Back to sign in
                    </Link>
                </p>

            </div>
        </div>
    );
}

export default ForgotPassword;
